import React, { useEffect, useMemo, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import SeoHead from './SeoHead';
import { blogPostPath, pagePaths } from '../utils/sitePaths';

const commonCta =
  'Need help planning your event in Gauteng? Message Ubuntu Events Management on WhatsApp for a fast, practical quote. Limited availability and weekend bookings fill fast.';

const posts = [
  {
    slug: 'catering-cost-south-africa',
    title: 'Catering Cost in South Africa: What You Should Budget for in 2026',
    excerpt: 'Budgeting guide for catering in Pretoria and Gauteng, including per-guest costs and hidden fees.',
    content: [
      'Catering cost in South Africa depends on guest numbers, menu style, service level, and logistics. In Pretoria and Gauteng, the most common mistake is budgeting only for food without accounting for staffing, transport, serving equipment, and setup time.',
      'For private events, buffet catering is usually the best value because it offers menu flexibility without high plated-service costs. For corporate events in Midrand or Johannesburg, speed and consistency often matter more than complexity, so practical menus perform better than expensive novelty options.',
      'A realistic way to budget is to break costs into clear categories: meal base price per guest, add-ons, staffing, equipment, and delivery zone. If your venue is far from the kitchen or has difficult access, transport and timing can affect final costs.',
      'Guest count also changes your cost efficiency. Smaller groups may have a higher per-person cost because fixed prep and transport are spread across fewer people. Medium and larger events generally unlock better cost efficiency, especially when menu choices are aligned with seasonality.',
      'If you are searching catering services near me in Pretoria, ask for a quote that includes everything: menu itemization, setup, serving, and cleanup. A transparent quote helps you compare vendors fairly and avoid hidden charges.',
      'Ubuntu Enterprises supports full-service event solutions, so catering can be bundled with decor, sound, and equipment hire. Bundling can reduce planning time and improve coordination, which often saves money indirectly by lowering delays and rework.',
      'When to book? Weekend dates in Gauteng fill quickly. If your function is in peak wedding season, confirm your booking as early as possible. Limited availability is real, and last-minute bookings usually reduce menu options.',
      'For events in Pretoria and Johannesburg, ask suppliers how they handle surge periods and staffing backups. The lowest quote can become expensive if service quality drops on the day.',
      'A practical budgeting benchmark is to reserve 10% contingency for guest growth, timing changes, or menu upgrades. This protects your event experience when plans shift.',
      'If your priority is premium guest experience, invest in menu quality, serving consistency, and smooth service flow before decorative extras. Guests remember food quality first.',
      'When you compare quotes, use the same event brief for every supplier so numbers are truly comparable. Include date, exact location, time window, and service expectations.',
      commonCta,
    ],
  },
  {
    slug: 'spit-braai-vs-buffet',
    title: 'Spit Braai vs Buffet: Which Option Fits Your Event Best?',
    excerpt: 'Compare spit braai and buffet catering for Gauteng events by atmosphere, cost, and guest flow.',
    content: [
      'Spit braai and buffet catering are both popular in Pretoria and Gauteng, but they solve different event goals. A spit braai creates a strong social atmosphere and visual impact. A buffet offers speed, variety, and easier dietary flexibility.',
      'If your event is outdoors and experience-driven, spit braai usually performs better. Guests enjoy the smell, the display, and the local feel. This works well for birthdays, family functions, and relaxed corporate team days.',
      'If your event is formal, tightly timed, or mixed-diet, buffet is often safer. You can serve vegetarian, protein-rich, and light options together with less queue friction. Buffets are also easier to scale for medium to large groups.',
      'Cost structure differs too. Spit braai may include machine hire, charcoal, and specialist staff. Buffet pricing is more linear around per-head menu value plus staffing and serving equipment.',
      'In Centurion and Midrand venues, site rules can influence your decision. Some venues limit open-flame equipment or require strict setup windows. Always check venue requirements before committing.',
      'A hybrid option works for many Gauteng event services clients: spit braai as the hero feature, supported by buffet sides and salads for variety. This balances experience and practicality.',
      'Ubuntu Enterprises can build a quote that compares both options for your specific guest size and event format, then align decor, sound hire, and setup timelines around your final choice.',
      commonCta,
    ],
  },
  {
    slug: 'how-to-plan-an-event-gauteng',
    title: 'How to Plan an Event in Gauteng Without Last-Minute Stress',
    excerpt: 'Step-by-step planning for weddings, corporate functions, and private events across Gauteng.',
    content: [
      'Planning an event in Pretoria becomes much easier when you work in clear phases. Start by fixing three anchors first: date, guest count range, and purpose of the event.',
      'Next, shortlist your service stack. Most events need at least catering, setup/decor, and venue coordination. If you have speeches, MCs, or music, include sound hire early to avoid last-minute technical fixes.',
      'Choose a venue area that is convenient for your guests. In Gauteng, traffic patterns from Johannesburg, Midrand, and Centurion can affect punctuality more than distance itself.',
      'Build your budget in layers: core essentials first, then experience upgrades. Core essentials include food, seating, tables, and basic coordination. Upgrades include signature decor moments, enhanced audio, and optional team experiences.',
      'For conversion-friendly planning, keep contact channels simple. A WhatsApp-first workflow gives you faster confirmations and clearer records of agreed details.',
      'Create a timeline backwards from event day: final menu lock, headcount confirmation, supplier alignment, and setup schedule. Include one buffer day where possible, especially for larger events.',
      'Work with a full-service event solutions company when you want less complexity. One team handling catering, decor, sound, equipment hire, and venue support usually means fewer communication gaps.',
      'Weekend bookings fill fast in Pretoria and Gauteng. If your date is fixed, secure it early and finalize details progressively.',
      'Create one primary communication channel for approvals and changes. A single WhatsApp thread keeps decisions visible and reduces confusion between multiple suppliers.',
      'Assign one decision-maker for final sign-off so key choices do not stall. Delayed decisions usually increase costs and reduce supplier availability.',
      'If your event includes guests from multiple Gauteng cities, share clear travel and arrival guidance in advance. This improves attendance and punctuality.',
      'Use a simple run-sheet for event day: setup time, food service time, speeches, entertainment, and close-down. Share this with all suppliers 48 hours in advance.',
      commonCta,
    ],
  },
  {
    slug: 'team-building-gauteng-ideas',
    title: 'Team Building Gauteng Ideas That Actually Improve Team Energy',
    excerpt: 'Practical team building formats for Pretoria, Midrand, and Johannesburg corporate groups.',
    content: [
      'Strong team building in Gauteng is not just about activities; it is about alignment with business outcomes. Good sessions improve trust, communication, and shared momentum after the event.',
      'Start with objective clarity. Are you onboarding, celebrating, resolving friction, or boosting motivation? The activity choice should match this objective, not trends.',
      'In Pretoria and Centurion, outdoor challenge formats work well for teams that need energy reset. In Midrand and Johannesburg, blended formats often work better for mixed teams with different comfort levels.',
      'Useful formats include low-pressure collaboration games, structured challenge rotations, and reflection-led wrap-ups. Add catering and relaxed social time after activities to reinforce connection.',
      'Measure impact simply: manager feedback, participant pulse, and one practical follow-up action per team. Without follow-up, even a great event loses value quickly.',
      'From a planning side, keep logistics practical: central location, realistic travel windows, clear weather alternatives, and accessible facilities.',
      'Ubuntu Enterprises supports team building as part of broader Gauteng event services, so you can bundle activity day planning with catering, decor, sound, and venue support when required.',
      commonCta,
    ],
  },
  {
    slug: 'event-hire-prices-south-africa',
    title: 'Event Hire Prices South Africa: Tents, Chairs, Tables and More',
    excerpt: 'What affects tent, chair, and table hire pricing in Pretoria and Gauteng, and how to compare quotes.',
    content: [
      'Event hire prices in South Africa vary based on quantity, transport distance, setup complexity, and event timing. In Pretoria and Gauteng, the biggest price differences usually come from logistics and availability, not only item rates.',
      'For tent, chair, and table hire, start with a layout-based estimate instead of guessing quantities. Your event format determines whether you need banquet, ceremony, lounge, or mixed seating.',
      'Always confirm what the quote includes: delivery, setup, breakdown, and collection timing. Low headline prices can become expensive if service components are billed separately.',
      'Venue constraints matter. Access hours, stairs, loading zones, and weather risk can influence labor and timing costs. A good provider highlights these risks before event day.',
      'If you are searching event hire near me in Pretoria, compare suppliers on reliability and communication, not only unit cost. Delays in hire setup can affect catering service and overall guest experience.',
      'Bundling hire with decor and catering can improve coordination. A full-service event solutions approach reduces handoff errors and often gives better timeline control.',
      'For Gauteng events, weekend demand is high and inventory moves quickly. Lock in key hire quantities early, then refine final numbers once RSVP counts stabilize.',
      'For weather-exposed events, include contingency items early: sidewalls, flooring, and backup power. These are cheaper when planned in advance than added at the last minute.',
      'When comparing event hire providers, ask for proof of delivery process, setup teams, and response protocol if something changes on event day.',
      'Hiring through one coordinated provider reduces supplier handover risks and keeps your timeline predictable from setup to breakdown.',
      'Event services Gauteng clients usually save the most when they finalize layout first, then lock hire quantities from that layout rather than estimating ad hoc.',
      commonCta,
    ],
  },
];

const mainServiceLinks = [
  {
    href: pagePaths.menu,
    label: 'Explore our Professional Event Catering Services',
  },
  {
    href: pagePaths.decor,
    label: 'View Event Decor and Styling Packages in Gauteng',
  },
  {
    href: pagePaths.sound,
    label: 'Explore Sound Hire and Event Audio Production',
  },
];

const postPrimaryLinks = {
  'catering-cost-south-africa': mainServiceLinks,
  'event-hire-prices-south-africa': mainServiceLinks,
};

const BlogPage = () => {
  const validSlugs = useMemo(() => new Set(posts.map((item) => item.slug)), []);
  const [active, setActive] = useState(null);

  const getBlogSlugFromPath = () => {
    const pathname = window.location.pathname;
    if (!pathname.startsWith('/blog/')) return null;
    const slug = decodeURIComponent(pathname.replace('/blog/', '').trim());
    return slug || null;
  };

  useEffect(() => {
    const syncFromPath = () => {
      const slug = getBlogSlugFromPath();
      if (slug && validSlugs.has(slug)) {
        setActive(slug);
      } else {
        setActive(null);
      }
    };
    syncFromPath();
    window.addEventListener('popstate', syncFromPath);
    return () => window.removeEventListener('popstate', syncFromPath);
  }, [validSlugs]);

  const selectPost = (slug) => {
    setActive(slug);
    const targetPath = blogPostPath(slug);
    if (window.location.pathname !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }
  };

  const post = active ? posts.find((item) => item.slug === active) : null;
  const canonicalPath = post ? blogPostPath(post.slug) : pagePaths.blog;
  const primaryLinks = post ? postPrimaryLinks[post.slug] || [] : [];

  return (
    <div className="min-h-screen py-10 px-4">
      <SeoHead
        title={
          post
            ? `${post.title} | Ubuntu Events Management`
            : 'Blog | Ubuntu Enterprises Event Guides'
        }
        description={
          post
            ? post.excerpt
            : 'Practical event planning guides for Pretoria and Gauteng: catering, spit braai, team building, and event hire pricing.'
        }
        canonicalPath={canonicalPath}
      />
      <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
        <aside className="card lg:col-span-1 h-fit">
          <h1 className="text-3xl font-playfair font-bold text-gray-800 mb-2">Blog</h1>
          <p className="text-sm text-gray-600 mb-4">
            <a href={pagePaths.blog} className="text-gold hover:underline font-medium">
              All articles
            </a>
            {' · '}
            <a href={pagePaths.home} className="text-gold hover:underline font-medium">
              Home
            </a>
          </p>
          <nav aria-label="Blog articles">
            <ul className="space-y-2">
              {posts.map((item) => (
                <li key={item.slug}>
                  <a
                    href={blogPostPath(item.slug)}
                    onClick={(e) => {
                      e.preventDefault();
                      selectPost(item.slug);
                    }}
                    className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      active === item.slug ? 'bg-gold/15 text-gold font-medium' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="lg:col-span-2 space-y-6">
          {!post ? (
            <section className="card">
              <h2 className="text-4xl font-playfair font-bold text-gray-800 mb-4">
                Event Planning Guides for Gauteng
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Practical guides on catering budgets, spit braai vs buffet, team building, event hire pricing, and
                stress-free planning in Pretoria, Johannesburg, and wider Gauteng.
              </p>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">All Articles</h3>
              <ul className="space-y-4">
                {posts.map((item) => (
                  <li key={item.slug} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                    <a
                      href={blogPostPath(item.slug)}
                      className="text-lg font-playfair font-semibold text-gold hover:underline"
                    >
                      {item.title}
                    </a>
                    <p className="text-gray-600 text-sm mt-1">{item.excerpt}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <article className="card">
              <p className="text-sm text-gray-500 mb-4">
                <a href={pagePaths.blog} className="text-gold hover:underline">
                  ← Back to Blog
                </a>
              </p>
              <h2 className="text-4xl font-playfair font-bold text-gray-800 mb-6">{post.title}</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Planning Insights for Gauteng Event Services</h3>
              {post.content.map((paragraph, idx) => (
                <p key={`${post.slug}-${idx}`} className="text-gray-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
              <div className="bg-gradient-to-r from-gold/10 to-olive/10 border border-gold/20 rounded-xl p-5 mb-8">
                <p className="text-gray-700 mb-3 font-medium">
                  Ubuntu Events Management is a full-service event solutions company offering catering, decor, sound
                  hire, equipment hire, team building, and venue support across Gauteng.
                </p>
                <a href={pagePaths.estimate} className="btn-primary text-sm py-2 px-4 inline-block">
                  Build Your Event in 2 Minutes
                </a>
              </div>

              {primaryLinks.length > 0 && (
                <section className="border-t border-gray-200 pt-8 mb-8">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Related Services</h3>
                  <ul className="space-y-3">
                    {primaryLinks.map((link) => (
                      <li key={link.label}>
                        <a href={link.href} className="text-gold font-medium hover:underline">
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="border-t border-gray-200 pt-8 mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">More Services</h3>
                <div className="flex flex-wrap gap-3">
                  <a href={pagePaths.menu} className="btn-secondary text-sm py-2 px-4">
                    Catering
                  </a>
                  <a href={pagePaths.decor} className="btn-secondary text-sm py-2 px-4">
                    Decor
                  </a>
                  <a href={pagePaths.sound} className="btn-secondary text-sm py-2 px-4">
                    Sound Hire
                  </a>
                </div>
              </section>

              <a
                href={`https://wa.me/27663286231?text=${encodeURIComponent(`Hi Ubuntu Events Management, I read your blog: ${post.title}. Please assist with a quote for my event in Gauteng.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
              >
                <MessageCircle size={18} />
                WhatsApp for Event Quote
              </a>
            </article>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
